import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  Identity,
  Invite,
  InviteResponseResult,
  Me,
  OrganizationUserPayload,
  Role,
} from '@ankor-io/common/types/account'
import type { UserWithConnectedAccounts } from '@ankor-io/common/types/awx'
import type { VesselUserPayload } from '@ankor-io/common/types/pay'

import { userService } from '@/api/services/user.service'
import { useUserContext } from '@/providers/user/useUserContext'

import { validateContextIds, validateInviteQueryParams, validateOrgId, validateVesselId } from './helpers'

// ========== Root-level Query Hooks ==========

// TODO: Implement users queries

/**
 * TanStack Query hook for fetching current user profile.
 * No context required - uses root-level endpoint.
 *
 * @example
 * ```ts
 * const { data: me, isLoading, error } = useGetMeQuery()
 * const { data: me, isLoading, error } = useGetMeQuery({ enabled: isAuthenticated })
 * ```
 */
export function useGetMeQuery(params?: { enabled?: boolean }) {
  const { enabled = true } = params || {}

  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => userService.getMe(),
    enabled,
  })
}

/**
 * TanStack Query hook for fetching an invite by ID.
 * No context or authentication required.
 *
 * @example
 * ```ts
 * const { data: result, isLoading, error } = useGetInviteQuery({ inviteId: 'invite_123' })
 * ```
 */
export function useGetInviteQuery(params: { inviteId: string; enabled?: boolean }) {
  const { inviteId, enabled = true } = params

  return useQuery<InviteResponseResult>({
    queryKey: ['invite', inviteId],
    queryFn: () => userService.getInvite(inviteId),
    enabled: enabled && Boolean(inviteId),
    staleTime: 0,
    gcTime: 0,
  })
}

// ========== Organization User Query Hooks ==========

/**
 * TanStack Query hook for fetching organization users list.
 * Automatically uses activeOrganizationId from context.
 *
 * @example
 * ```ts
 * const { data: users, isLoading, error } = useListOrganizationUsersQuery({ take: 10, skip: 0 })
 * ```
 */
export function useListOrganizationUsersQuery(params?: { take?: number; skip?: number; enabled?: boolean }) {
  const { activeOrganizationId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId)

  return useQuery<{ identity: Identity; roles: Role[] }[]>({
    queryKey: ['organization-users', activeOrganizationId, listParams],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      return userService.listOrganizationUsers(activeOrganizationId, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching organization users with vessel access (identity, roles, vessels with connected accounts).
 * Automatically uses activeOrganizationId from context.
 *
 * @example
 * ```ts
 * const { data: users, isLoading, error } = useListOrganizationUsersWithAccessQuery({ take: 50, skip: 0 })
 * ```
 */
export function useListOrganizationUsersWithAccessQuery(params?: { take?: number; skip?: number; enabled?: boolean }) {
  const { activeOrganizationId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId)

  return useQuery<OrganizationUserPayload[]>({
    queryKey: ['organization-users-with-access', activeOrganizationId, listParams],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      return userService.listOrganizationUsersWithAccess(activeOrganizationId, listParams)
    },
    enabled: isEnabled,
  })
}

const ORGANIZATION_USERS_WITH_ACCESS_PAGE_SIZE = 20

/**
 * TanStack Infinite Query hook for organization users with vessel access (load-more pagination).
 * Use this for list UIs that need "Load more" or infinite scroll.
 *
 * @example
 * ```ts
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useListOrganizationUsersWithAccessInfiniteQuery()
 * const users = data?.pages.flat() ?? []
 * ```
 */
export function useListOrganizationUsersWithAccessInfiniteQuery(params?: { take?: number; enabled?: boolean }) {
  const { activeOrganizationId } = useUserContext()
  const { enabled = true, take = ORGANIZATION_USERS_WITH_ACCESS_PAGE_SIZE } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId)

  return useInfiniteQuery<OrganizationUserPayload[]>({
    queryKey: ['organization-users-with-access', activeOrganizationId, { take }],
    queryFn: ({ pageParam = 0 }) => {
      validateOrgId(activeOrganizationId)
      return userService.listOrganizationUsersWithAccess(activeOrganizationId, {
        take,
        skip: pageParam as number,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < take) return undefined
      return allPages.length * take
    },
    initialPageParam: 0,
    enabled: isEnabled,
  })
}

const CONNECTED_ACCOUNT_USERS_WITH_MIN_ROLE_PAGE_SIZE = 20

/**
 * TanStack Infinite Query hook for connected account users with minimum role (load-more pagination).
 * Use this for list UIs that need "Load more" or infinite scroll.
 *
 * @example
 * ```ts
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useListConnectedAccountUsersWithMinRoleQuery()
 * const users = data?.pages.flat() ?? []
 * ```
 */
export function useListConnectedAccountUsersWithMinRoleQuery(params?: {
  minRole: Role
  take?: number
  skip?: number
  enabled?: boolean
}) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { enabled = true, minRole, take = CONNECTED_ACCOUNT_USERS_WITH_MIN_ROLE_PAGE_SIZE, skip } = params || {}

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useInfiniteQuery<{ identity: Identity; roles: Role[] }[]>({
    queryKey: [
      'connected-account-users-with-min-role',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      { minRole, take, skip },
    ],
    queryFn: ({ pageParam = 0 }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return userService.listConnectedAccountUsersWithMinRole(orgId, vesselId, connectedAccountId, {
        minRole,
        take,
        skip: pageParam as number,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < take) return undefined
      return allPages.length * take
    },
    initialPageParam: 0,
    enabled: isEnabled,
  })
}

/**
 * TanStack Query mutation hook for updating organization user roles.
 * Automatically uses activeOrganizationId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const updateRoles = useUpdateOrganizationUserRolesMutation()
 * updateRoles.mutate({
 *   userId: 'user_123',
 *   payload: { roles: [Role.ADMIN] }
 * })
 * ```
 */
export function useUpdateOrganizationUserRolesMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async ({ userId, payload }: { userId: string; payload: { roles: Role[] } }) => {
      validateOrgId(activeOrganizationId)
      return userService.updateOrganizationUserRoles(activeOrganizationId, userId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['organization-users-with-access', activeOrganizationId],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for removing a user from an organization.
 * Automatically uses activeOrganizationId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const removeUser = useRemoveOrganizationUserMutation()
 * removeUser.mutate({ userId: 'user_123' })
 * ```
 */
export function useRemoveOrganizationUserMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      validateOrgId(activeOrganizationId)
      return userService.removeOrganizationUser(activeOrganizationId, userId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['organization-users-with-access', activeOrganizationId],
      })
    },
  })
}

// ========== Organization Invite Query Hooks ==========

/**
 * TanStack Query hook for fetching organization invites list.
 * Automatically uses activeOrganizationId from context.
 *
 * @example
 * ```ts
 * const { data: invites, isLoading, error } = useListOrganizationInvitesQuery({ take: 10, skip: 0 })
 * ```
 */
export function useListOrganizationInvitesQuery(params?: { take?: number; skip?: number; enabled?: boolean }) {
  const { activeOrganizationId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId)

  return useQuery<Invite[]>({
    queryKey: ['organization-invites', activeOrganizationId, listParams],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      return userService.listOrganizationInvites(activeOrganizationId, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query mutation hook for creating an organization invite.
 * Automatically uses activeOrganizationId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const createInvite = useCreateOrganizationInviteMutation()
 * createInvite.mutate({
 *   email: 'user@example.com',
 *   roles: [Role.ADMIN],
 *   awxConnectedAccountId: 'ca_123'
 * })
 * ```
 */
export function useCreateOrganizationInviteMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: { email: string; roles: Role[]; awxConnectedAccountId?: string }) => {
      validateOrgId(activeOrganizationId)
      return userService.createOrganizationInvite(activeOrganizationId, payload)
    },
    onSuccess: () => {
      // Invalidate organization invites list queries
      queryClient.invalidateQueries({
        queryKey: ['organization-invites', activeOrganizationId],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for creating a vessel invite with module access.
 * Invalidates organization invites and connected-account invites lists on success.
 */
export function useCreateVesselInviteMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      vesselId,
      payload,
    }: {
      vesselId: string
      payload: { email: string; roles: Role[]; awxConnectedAccountIds: string[]; connectionId?: string }
    }) => {
      validateOrgId(activeOrganizationId)
      return userService.createVesselInvite(activeOrganizationId, vesselId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-invites', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['vessel-invites'],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for deleting an organization invite.
 * Automatically uses activeOrganizationId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const deleteInvite = useDeleteOrganizationInviteMutation()
 * deleteInvite.mutate({ inviteId: 'invite_123' })
 * ```
 */
export function useDeleteOrganizationInviteMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId } = useUserContext()

  return useMutation({
    mutationFn: async ({ inviteId }: { inviteId: string }) => {
      const { orgId, inviteId: validatedInviteId } = validateInviteQueryParams(activeOrganizationId, inviteId)
      return userService.deleteOrganizationInvite(orgId, validatedInviteId)
    },
    onSuccess: () => {
      // Invalidate organization invites list queries
      queryClient.invalidateQueries({
        queryKey: ['organization-invites', activeOrganizationId],
      })

      // Invalidate vessel invites list queries
      queryClient.invalidateQueries({
        queryKey: ['vessel-invites', activeOrganizationId, activeVesselId],
      })
    },
  })
}

// ========== User Access Query Hooks ==========

/**
 * TanStack Query hook for fetching user access to connected accounts.
 * Automatically uses activeOrganizationId from context.
 *
 * @example
 * ```ts
 * const { data: users, isLoading, error } = useGetUserAccessToConnectedAccountsQuery({ take: 10, skip: 0 })
 * ```
 */
export function useGetUserAccessToConnectedAccountsQuery(params?: { take?: number; skip?: number; enabled?: boolean }) {
  const { activeOrganizationId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId)

  return useQuery<UserWithConnectedAccounts[]>({
    queryKey: ['user-access-to-connected-accounts', activeOrganizationId, listParams],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      return userService.getUserAccessToConnectedAccounts(activeOrganizationId, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query mutation hook for adding a user to multiple connected accounts.
 * Automatically uses activeOrganizationId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const addUser = useAddUserToConnectedAccountsMutation()
 * addUser.mutate({
 *   userId: 'user_123',
 *   connectedAccountIds: ['ca_456', 'ca_789']
 * })
 * ```
 */
export function useAddUserToConnectedAccountsMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: { userId: string; connectedAccountIds: string[]; skipInvalidation?: boolean }) => {
      validateOrgId(activeOrganizationId)
      return userService.addUserToConnectedAccounts(activeOrganizationId, {
        userId: payload.userId,
        connectedAccountIds: payload.connectedAccountIds,
      })
    },
    onSuccess: (_data, variables) => {
      if (variables.skipInvalidation) {
        return
      }

      queryClient.invalidateQueries({
        queryKey: ['user-access-to-connected-accounts', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['organization-users', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['organization-users-with-access', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['vessel-users', activeOrganizationId],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for removing a user from multiple connected accounts.
 * Automatically uses activeOrganizationId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const removeUser = useRemoveUserFromConnectedAccountsMutation()
 * removeUser.mutate({
 *   userId: 'user_123',
 *   connectedAccountIds: ['ca_456']
 * })
 * ```
 */
export function useRemoveUserFromConnectedAccountsMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: { userId: string; connectedAccountIds: string[] }) => {
      validateOrgId(activeOrganizationId)
      return userService.removeUserFromConnectedAccounts(activeOrganizationId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user-access-to-connected-accounts', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['organization-users', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['organization-users-with-access', activeOrganizationId],
      })
      queryClient.invalidateQueries({
        queryKey: ['vessel-users', activeOrganizationId],
      })
    },
  })
}

// ========== Vessel User Query Hooks ==========

/**
 * TanStack Query hook for fetching vessel users list.
 * Automatically uses activeOrganizationId, activeVesselId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: users, isLoading, error } = useListVesselUsersQuery({ take: 10, skip: 0 })
 * ```
 */
export function useListVesselUsersQuery(params?: { take?: number; skip?: number; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useQuery<VesselUserPayload[]>({
    queryKey: ['vessel-users', activeOrganizationId, activeVesselId, activeConnectedAccountId, listParams],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      validateVesselId(activeVesselId)
      return userService.listVesselUsers(activeOrganizationId, activeVesselId, listParams)
    },
    enabled: isEnabled,
  })
}

const VESSEL_USERS_PAGE_SIZE = 20

/**
 * TanStack Infinite Query hook for vessel users (load-more pagination).
 * Use this for list UIs that need "Load more" or infinite scroll.
 *
 * @example
 * ```ts
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useListVesselUsersInfiniteQuery()
 * const users = data?.pages.flat() ?? []
 * ```
 */
export function useListVesselUsersInfiniteQuery(params?: { take?: number; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { enabled = true, take = VESSEL_USERS_PAGE_SIZE } = params || {}

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useInfiniteQuery<VesselUserPayload[]>({
    queryKey: ['vessel-users', activeOrganizationId, activeVesselId, activeConnectedAccountId, { take }],
    queryFn: ({ pageParam = 0 }) => {
      validateOrgId(activeOrganizationId)
      validateVesselId(activeVesselId)
      return userService.listVesselUsers(activeOrganizationId, activeVesselId, {
        take,
        skip: pageParam as number,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < take) return undefined
      return allPages.length * take
    },
    initialPageParam: 0,
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching vessel in-progress invites list.
 * Uses org + vessel scoped endpoint (no connected account required).
 *
 * @example
 * ```ts
 * const { data: invites, isLoading, error } = useListVesselInvitesQuery({ take: 10, skip: 0 })
 * ```
 */
export function useListVesselInvitesQuery(params?: { take?: number; skip?: number; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled = enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId)

  return useQuery<Invite[]>({
    queryKey: ['vessel-invites', activeOrganizationId, activeVesselId, listParams],
    queryFn: () => {
      validateOrgId(activeOrganizationId)
      validateVesselId(activeVesselId)
      return userService.listVesselInProgressInvites(activeOrganizationId!, activeVesselId!, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * CURRENTLY UNUSED
 * TanStack Query mutation hook for adding users to a connected account.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const addUsers = useAddUsersToConnectedAccountMutation()
 * addUsers.mutate({
 *   userIds: ['user_123', 'user_456']
 * })
 * ```
 */
export function useAddUsersToConnectedAccountMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: { userIds: string[] }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return userService.addUsersToConnectedAccount(orgId, vesselId, connectedAccountId, payload)
    },
    onSuccess: () => {
      // Invalidate vessel users list queries
      queryClient.invalidateQueries({
        queryKey: ['vessel-users', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
    },
  })
}

/**
 * CURRENTLY UNUSED
 * TanStack Query mutation hook for removing users from a connected account.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const removeUsers = useRemoveUsersFromConnectedAccountMutation()
 * removeUsers.mutate({
 *   userIds: ['user_123']
 * })
 * ```
 */
export function useRemoveUsersFromConnectedAccountMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: { userIds: string[] }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return userService.removeUsersFromConnectedAccount(orgId, vesselId, connectedAccountId, payload)
    },
    onSuccess: () => {
      // Invalidate vessel users list queries
      queryClient.invalidateQueries({
        queryKey: ['vessel-users', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
    },
  })
}
